
import moment from 'moment';
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import VisitorsSmsToken from 'Models/Security/Acl/VisitorsSmsToken';
import UserSmsToken from 'Models/Security/Acl/UserSmsToken';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface ISmsTokenEntityAttributes extends IModelAttributes {
	token: number;
	expiry: Date;
	tokencount: number;

	esignceremonysignee?: Models.EsignceremonysigneeEntity |
		Models.IEsignceremonysigneeEntityAttributes;
	esignceremonywitness?: Models.EsignceremonywitnessEntity |
		Models.IEsignceremonywitnessEntityAttributes;

}

@entity('SmsTokenEntity', 'SMS Token')
export default class SmsTokenEntity extends Model
	implements ISmsTokenEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsSmsToken(),
		new UserSmsToken(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	
	@Validators.Integer()
	@observable
	@attribute<SmsTokenEntity, number>()
	@CRUD({
		name: 'Token',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public token: number;

	@observable
	@attribute<SmsTokenEntity, Date>()
	@CRUD({
		name: 'Expiry',
		displayType: 'datetimepicker',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseDate,
	})
	public expiry: Date;
	

	
	@Validators.Integer()
	@observable
	@attribute<SmsTokenEntity, number>()
	@CRUD({
		name: 'TokenCount',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public tokencount: number;

	@observable
	@attribute({ isReference: true, manyReference: false })
	@CRUD({
		
		name: 'ESignCeremonySignee',
		displayType: 'displayfield',
		order: 40,
		inputProps: {
			displayFunction: (model?: Models.EsignceremonysigneeEntity) => model ? model.getDisplayName() : null,
		},
		
	})
	public esignceremonysignee?: Models.EsignceremonysigneeEntity;

	@observable
	@attribute({ isReference: true, manyReference: false })
	@CRUD({
		
		name: 'ESignCeremonyWitness',
		displayType: 'displayfield',
		order: 50,
		inputProps: {
			displayFunction: (model?: Models.EsignceremonywitnessEntity) => model ? model.getDisplayName() : null,
		},
		
	})
	public esignceremonywitness?: Models.EsignceremonywitnessEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<ISmsTokenEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<ISmsTokenEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.token !== undefined) {
				this.token = attributes.token;
			}
			if (attributes.expiry !== undefined) {
				if (attributes.expiry === null) {
					this.expiry = attributes.expiry;
				} else {
					this.expiry = moment(attributes.expiry).toDate();
				}
			}
			if (attributes.tokencount !== undefined) {
				this.tokencount = attributes.tokencount;
			}
			if (attributes.esignceremonysignee !== undefined) {
				if (attributes.esignceremonysignee === null) {
					this.esignceremonysignee = attributes.esignceremonysignee;
				} else if (attributes.esignceremonysignee instanceof Models.EsignceremonysigneeEntity) {
					this.esignceremonysignee = attributes.esignceremonysignee;
				} else {
					this.esignceremonysignee = new Models.EsignceremonysigneeEntity(attributes.esignceremonysignee);
				}
			}
			if (attributes.esignceremonywitness !== undefined) {
				if (attributes.esignceremonywitness === null) {
					this.esignceremonywitness = attributes.esignceremonywitness;
				} else if (attributes.esignceremonywitness instanceof Models.EsignceremonywitnessEntity) {
					this.esignceremonywitness = attributes.esignceremonywitness;
				} else {
					this.esignceremonywitness = new Models.EsignceremonywitnessEntity(attributes.esignceremonywitness);
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		esignceremonysignee {
			${Models.EsignceremonysigneeEntity.getAttributes().join('\n')}
		}
		esignceremonywitness {
			${Models.EsignceremonywitnessEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(SmsTokenEntity.prototype, 'created');
CRUD(modifiedAttr)(SmsTokenEntity.prototype, 'modified');
